// Лентата с търсачката на листа „Изписване": голямата търсачка „намери и
// добави" (търси едновременно ПРОДУКТИ и РЕЦЕПТИ; Enter/клик добавя НОВ ред от
// съответния вид) + „Целият списък" (каталогът с раздел „Рецепти"). Клавишите
// на полето са в общия `useWorksheetSearch`.
import { useEffect } from "react";
import { Input } from "antd";
import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatQty } from "../../components/qty";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { highlightWords, type WriteoffSearchResult } from "./writeoffWorksheetShared";

// колони на падащия списък: име · наличност
const DROPDOWN_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_200px] gap-[18px]";

type Props = {
  attachInputRef: (node: HTMLInputElement | null) => void;
  value: string;
  onValueChange: (value: string) => void;
  onFocusChange: (focused: boolean) => void;
  onInputKeyDown: (e: React.KeyboardEvent) => void;
  dropdownOpen: boolean;
  results: WriteoffSearchResult[];
  searchWords: string[];
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onPick: (result: WriteoffSearchResult) => void;
  onOpenCatalog: () => void;
  // 16px на телефон — под 16px iOS Safari зуумва страницата при фокус
  isNarrow: boolean;
};

export const WriteoffWorksheetSearch = ({
  attachInputRef,
  value,
  onValueChange,
  onFocusChange,
  onInputKeyDown,
  dropdownOpen,
  results,
  searchWords,
  highlightedIndex,
  onHighlightChange,
  onPick,
  onOpenCatalog,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  // Маркираният резултат винаги остава във видимата част на списъка
  useEffect(() => {
    if (!dropdownOpen) return;
    document.getElementById(`wox-opt-${highlightedIndex}`)?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, dropdownOpen]);

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <Input
          ref={(node) => attachInputRef(node?.input ?? null)}
          allowClear
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onFocus={() => onFocusChange(true)}
          onBlur={() => onFocusChange(false)}
          onKeyDown={onInputKeyDown}
          prefix={<SearchOutlined className="mr-1 text-[17px] text-muted" />}
          placeholder={t("inventoryPage.woSearchPh")}
          // ! — antd cssinjs е извън Tailwind layer-ите и иначе бие класа
          className={`w-full !rounded-[10px] ${isNarrow ? "!text-base" : "!text-[15px]"}`}
        />
        {dropdownOpen && (
          <div className="dk-elevated absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-shade-3 bg-surface shadow-[0_18px_44px_rgba(20,15,8,0.16),0_4px_12px_rgba(20,15,8,0.08)]">
            <div
              className={`${DROPDOWN_GRID_CLASS} items-center border-b border-shade-2 bg-fill px-[18px] py-2 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted`}
            >
              <span className="truncate">{t("inventoryPage.wsResultsCount", { count: results.length })}</span>
              <span className="whitespace-nowrap text-right">{t("inventoryPage.productOptStock")}</span>
            </div>
            <div className="max-h-[min(46vh,430px)] overflow-y-auto">
              {results.map((result, index) => (
                <div
                  key={`${result.kind}-${result.id}`}
                  id={`wox-opt-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPick(result)}
                  onMouseMove={() => onHighlightChange(index)}
                  className={`${DROPDOWN_GRID_CLASS} cursor-pointer items-center border-b border-shade-1 px-[18px] py-[11px] ${
                    index === highlightedIndex ? "bg-brand-tint" : "bg-surface"
                  }`}
                >
                  {/* ПРОДУКТ / РЕЦЕПТА */}
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="truncate text-[15px] font-semibold text-ink">
                      {highlightWords(result.name, searchWords)}
                    </span>
                    {result.kind === "product" && result.unit && (
                      <span className="shrink-0 text-xs text-muted">{result.unit}</span>
                    )}
                    {result.kind === "recipe" && (
                      <span className="shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] py-px pb-[3px] text-[11px] font-semibold text-brand-deep-ink">
                        {t("inventoryPage.recipe")}
                      </span>
                    )}
                  </div>
                  {/* НАЛИЧНОСТ (клампната до 0) */}
                  <div className="whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-semibold text-body">
                    {result.kind === "product" ? (
                      <>
                        {formatQty(Math.max(0, result.available), result.decimals)}
                        {result.unit ? (
                          <span className="font-sans text-xs font-normal text-muted"> {result.unit}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-ghost">—</span>
                    )}
                  </div>
                </div>
              ))}
              {results.length === 0 && (
                <div className="px-4 py-[18px] text-[12.5px] text-muted">{t("inventoryPage.wsNoMatch")}</div>
              )}
            </div>
          </div>
        )}
      </div>
      <button type="button" onClick={onOpenCatalog} className={`${SECONDARY_BUTTON_CLASS} !py-[9px]`}>
        <AppstoreOutlined />
        {t("inventoryPage.catalogOpen")}
      </button>
    </div>
  );
};

export default WriteoffWorksheetSearch;
